import { type FC, useMemo, useState } from 'react'

import { useQuery } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import { IconSortAsc } from 'assets/svg/sprite'
import SearchField from 'components/Form/SearchField/SearchField'
import ViewersList from 'features/LiveStream/ViewersList/ViewersList'
import { type ViewerItem, fetchViewers } from 'services/endpoints/streaming'

import LiveModal from '../LiveModal'

import './_viewersModal.scss'

interface Props {
  onClose: () => void
  username?: string
  creatorId?: string
}

type SortMode = 'joined' | 'tipped'

const formatMoney = (amount: number) => {
  return `$${Number(amount ?? 0)
    .toFixed(2)
    .replace(/\.00$/, '')}`
}

const getDisplayName = (viewer: ViewerItem) => {
  if (viewer.name?.trim()) return viewer.name.trim()
  if (viewer.username?.trim()) return viewer.username.trim()
  return 'Unknown user'
}

const getHandle = (viewer: ViewerItem) => {
  if (!viewer.username?.trim()) return '@unknown'

  return viewer.username.startsWith('@')
    ? viewer.username
    : `@${viewer.username}`
}

const ViewersModal: FC<Props> = ({ onClose, username, creatorId }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [page] = useState(1)
  const [sortMode, setSortMode] = useState<SortMode>('tipped')

  const queryEnabled = Boolean(username ?? creatorId)

  const { data, isLoading, isError } = useQuery(
    ['stream-viewers', username, creatorId, page],
    async () =>
      await fetchViewers({
        username,
        creator_id: creatorId,
        page,
      }),
    {
      enabled: queryEnabled,
      keepPreviousData: true,
    }
  )

  const filteredAndSortedViewers = useMemo(() => {
    const viewers = data?.viewers ?? []
    const normalizedSearch = searchTerm.trim().toLowerCase()

    const filtered = !normalizedSearch
      ? viewers
      : viewers.filter((viewer: ViewerItem) => {
          const usernameValue = viewer.username?.toLowerCase() ?? ''
          const nameValue = viewer.name?.toLowerCase() ?? ''

          return (
            usernameValue.includes(normalizedSearch) ||
            nameValue.includes(normalizedSearch)
          )
        })

    return [...filtered].sort((a: ViewerItem, b: ViewerItem) => {
      if (sortMode === 'tipped') {
        return Number(b.tipped_amount ?? 0) - Number(a.tipped_amount ?? 0)
      }

      return new Date(b.joined_at).getTime() - new Date(a.joined_at).getTime()
    })
  }, [data?.viewers, searchTerm, sortMode])

  const viewersListItems = useMemo(() => {
    return filteredAndSortedViewers.map((viewer: ViewerItem) => ({
      id:
        viewer.id ??
        viewer.viewer_id ??
        viewer.user_id ??
        `${viewer.joined_at}-${viewer.username ?? 'unknown'}`,
      avatarUrl: viewer.avatar ?? placeholderAvatar,
      name: getDisplayName(viewer),
      handle: getHandle(viewer),
      tipped: formatMoney(viewer.tipped_amount),
    }))
  }, [filteredAndSortedViewers])

  const toggleSort = () => {
    setSortMode(prev => (prev === 'tipped' ? 'joined' : 'tipped'))
  }

  return (
    <LiveModal title='Viewers' onClose={onClose}>
      <div className='viewersmodal'>
        <div className='viewersmodal__header'>
          <SearchField
            value={searchTerm}
            changeFn={(term: string) => {
              setSearchTerm(term)
            }}
            clearFn={() => {
              setSearchTerm('')
            }}
            additionalProps={{ placeholder: 'Search users' }}
          />

          <div
            className='viewersmodal__header--sort'
            onClick={toggleSort}
            role='button'
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggleSort()
              }
            }}
            title={
              sortMode === 'tipped'
                ? 'Sorted by tipped amount'
                : 'Sorted by joined time'
            }
          >
            <IconSortAsc />
          </div>
        </div>

        {isLoading && (
        <div className='viewersmodal__state viewersmodal__loader'>
            <BeatLoader size={8} color="#fff" />
        </div>
        )}

        {isError && (
          <div className='viewersmodal__state'>Failed to load viewers.</div>
        )}

        {!isLoading && !isError && !data?.active && (
          <div className='viewersmodal__state'>No active stream.</div>
        )}

        {!isLoading &&
          !isError &&
          data?.active &&
          viewersListItems.length === 0 && (
            <div className='viewersmodal__state'>
              {searchTerm.trim()
                ? 'No matching viewers found.'
                : 'No viewers found.'}
            </div>
          )}

        {!isLoading &&
          !isError &&
          data?.active &&
          viewersListItems.length > 0 && (
            <ViewersList viewers={viewersListItems} type='viewers' />
          )}
      </div>
    </LiveModal>
  )
}

export default ViewersModal